
import React from "react";
import { useLists } from "@/context/ListContext";
import { useTasks } from "@/context/TaskContext";
import { Button } from "../ui/button";
import ListItem from "./ListItem";
import { InputWithEmojiPicker } from "@/components/ui/InputWithEmojiPicker";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import { useAuth } from "@/context/AuthContext";
import { useLocalTaskLimits } from "@/hooks/useLocalTaskLimits";

// Utility to generate fallback priority counts for lists with no tasks (or for lists without those stats)
const getDefaultPriorityCounts = () => ({
  "do-first": 0,
  "schedule": 0,
  "delegate": 0,
  "eliminate": 0,
  "unprioritized": 0,
});

// Count tasks and "priority" fields for the Eisenhower view -- fallback to 0 if not present
function getPriorityCounts(tasks, listId) {
  const filtered = tasks.filter(t => t.list_id === listId);
  const counts = getDefaultPriorityCounts();
  filtered.forEach((t) => {
    switch (t.priority) {
      case "do-first":
        counts["do-first"]++;
        break;
      case "schedule":
        counts["schedule"]++;
        break;
      case "delegate":
        counts["delegate"]++;
        break;
      case "eliminate":
        counts["eliminate"]++;
        break;
      default:
        counts["unprioritized"]++;
    }
  });
  return counts;
}

const ListSidebar = React.memo(function ListSidebar(
  { switching = false, activeListId = null, onItemSelected, onShowListCreationNudge }: { 
    switching?: boolean, 
    activeListId?: string | null, 
    onItemSelected?: () => void,
    onShowListCreationNudge?: () => void
  }
) {
  const { lists, activeListId: _activeListId, setActiveListId, addList, updateList, deleteList, reorderLists, loading } = useLists();
  const { tasks } = useTasks();
  const { session } = useAuth();
  const { canCreateList } = useLocalTaskLimits();
  const currentListId = activeListId || _activeListId;

  const [creating, setCreating] = React.useState(false);
  const [newName, setNewName] = React.useState("");

  const handleSelect = React.useCallback((id: string) => {
    setActiveListId(id);
    onItemSelected?.();
  }, [setActiveListId, onItemSelected]);
  const handleEdit = React.useCallback((id: string, name: string) => updateList(id, name), [updateList]);
  const handleDelete = React.useCallback((id: string) => deleteList(id), [deleteList]);
  const handleCreate = React.useCallback(async () => {
    if (!newName.trim()) return;
    await addList(newName);
    setNewName("");
    setCreating(false);
  }, [newName, addList]);

  // --- NEW: drag and drop ---
  const handleDragEnd = React.useCallback(
    (result: DropResult) => {
      const { destination, source } = result;
      if (!destination) return;
      if (destination.index === source.index) return;
      reorderLists(source.index, destination.index);
    },
    [reorderLists]
  );

  if (loading) {
    return (
      <div className="flex flex-col h-full bg-sidebar-background animate-pulse px-4 pt-6">
        <div className="h-5 w-24 bg-gray-200 dark:bg-gray-800 rounded mb-4" />
        <div className="flex-1">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-gray-100 dark:bg-gray-700 rounded-xl h-20 mb-3" />
          ))}
        </div>
        <div className="h-10 w-full bg-gray-200 dark:bg-gray-800 rounded mb-2" />
      </div>
    );
  }

  // --- new: calculate total lists (after loading)
  const totalLists = lists.length;

  return (
    <div className="flex flex-col h-full bg-sidebar-background">
      <div className="relative flex items-center px-4 pt-6 pb-4 border-b border-gray-100 dark:border-gray-800">
        {/* Title left, button right */}
        <span className="font-bold text-2xl text-gray-900 dark:text-white font-lora">
          Lists
        </span>
        <div className="flex-1" />
        <Button
          variant="outline"
          size="sm"
          className="px-3"
          onClick={() => {
            if (!session && !canCreateList()) {
              onShowListCreationNudge?.();
              return;
            }
            setCreating((v) => !v);
          }}
        >
          New list
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto py-2 px-2 flex flex-col gap-2">
        {/* Creation form appears at the very top, pushes list items down */}
        {creating && (
          <div className="bg-white dark:bg-[#1A1F2C] rounded-lg p-4 mb-2 border border-gray-200 dark:border-white/10 flex flex-col gap-2">
            <InputWithEmojiPicker
              id="list-title"
              placeholder="List name"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              className="w-full rounded px-3 py-2 border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring focus:ring-blue-300 dark:bg-gray-900 dark:text-white"
              maxLength={50}
              autoFocus
              onKeyDown={e => e.key === "Enter" && handleCreate()}
            />
            <div className="flex gap-2 mt-1">
              <Button
                size="sm"
                className="flex-1"
                onClick={handleCreate}
                disabled={!newName.trim()}
              >
                Create
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="flex-1"
                onClick={() => {
                  setNewName("");
                  setCreating(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
        {/* --- DragDrop section for lists --- */}
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="sidebar-lists-droppable">
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="flex flex-col gap-2"
              >
                {lists.map((list, i) => {
                  const isActive = list.id === currentListId;
                  // const taskCount = tasks.filter(t => t.list_id === list.id).length;
                  // const priorityCounts = getPriorityCounts(tasks, list.id);
                  return (
                    <Draggable key={list.id} draggableId={list.id} index={i}>
                      {(draggableProvided, draggableSnapshot) => (
                        <div
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          {...draggableProvided.dragHandleProps}
                          style={{
                            ...draggableProvided.draggableProps.style,
                            // Optional: add a little lift while dragging
                            boxShadow: draggableSnapshot.isDragging
                              ? "0 4px 20px 2px rgba(60,60,60,0.12)"
                              : undefined,
                            cursor: "grab",
                          }}
                        >
                          <ListItem
                            list={list}
                            activeListId={currentListId}
                            onSelect={handleSelect}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            // taskCount={taskCount}
                            // priorityCounts={priorityCounts}
                            index={i}
                            totalLists={totalLists}
                          />
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        {lists.length === 0 && !creating && (
          <div className="text-sm text-center text-gray-400 mt-8">No lists yet.</div>
        )}
      </div>
    </div>
  );
});
export default ListSidebar;

